import { FC } from 'react'
import { RewardType } from '@g4g/graphql/src/shop/__generated__/globalTypes'
import RewardProgressBarIcon from './RewardProgressBarIcon'
import type { RewardProgress } from '@g4g/utils/src/types'
import { classNames } from '@g4g/utils/src/react/class-names'
import { getLastLvlRequirement, vipExpBgColor, vipExpColor } from '@g4g/utils/src/gameDataUtils'
import { ShoppingCartIcon } from '@heroicons/react/20/solid'

const Stats: FC<{ progress: RewardProgress; type: RewardType.SalePoints | RewardType.VipExp }> = ({
  progress,
  type,
}) => {
  const hasCartProgress = (progress?.cartPctg && progress.cartPctg > 0) || progress?.isOverNextDing
  const maxDing = getLastLvlRequirement().nextLvlEntryXp

  return (
    <div className="flex items-center justify-center text-sm text-white">
      {hasCartProgress && (
        <div className="mx-1">
          <ShoppingCartIcon className="w-3.5 h-3.5 text-shopStdGreen" />
        </div>
      )}
      <span
        data-cy="shop-rewards-progress-amount"
        data-cy-spec={type}
        style={{
          ...(type === RewardType.VipExp &&
            !hasCartProgress && { color: vipExpColor(progress.amount ?? 0) }),
        }}
        className={classNames(hasCartProgress ? 'text-shopStdGreen' : 'text-orange-400')}
      >
        {progress?.amount}
      </span>
      {progress.nextDing !== maxDing && (
        <>
          <span className="mx-1">/</span>
          <span data-cy="shop-rewards-progress-next-ding" data-cy-spec={type}>
            {progress?.nextDing}
          </span>
        </>
      )}
    </div>
  )
}

const Bar: FC<{
  gamePctg: number
  cartPctg: number
  type: RewardType.SalePoints | RewardType.VipExp
  amount: number
}> = ({ gamePctg, cartPctg, type, amount }) => (
  <div className="relative flex w-full border border-white border-opacity-20">
    <div
      data-cy="shop-rewards-progress-bar-game"
      data-cy-spec={type}
      style={{
        width: `${gamePctg}%`,
        backgroundColor: type === RewardType.SalePoints ? '#96702d' : vipExpBgColor(amount),
      }}
    ></div>
    <div className="flex-grow h-5">
      <div
        data-cy="shop-rewards-progress-bar-cart"
        data-cy-spec={type}
        className="h-full origin-left"
        style={{
          // this format translate(0px) scaleX(0) is needed for Safari to work
          transform: `translate(0px) scaleX(${cartPctg / 100})`,
          backgroundColor: '#466d50',
        }}
      ></div>
    </div>
  </div>
)

const RewardProgressBar: FC<{
  type: RewardType.SalePoints | RewardType.VipExp
  progress: RewardProgress
}> = ({ progress, type }) => (
  <div className="relative w-full">
    <div className="absolute z-10 -left-8 -top-4">
      <RewardProgressBarIcon amount={progress.amount} side="start" type={type} size={50} />
    </div>
    {progress.cartPctg !== undefined && progress.gamePctg !== undefined && (
      <>
        <Bar
          cartPctg={progress.cartPctg}
          gamePctg={progress.gamePctg}
          type={type}
          amount={progress.amount ?? 0}
        />
        <Stats progress={progress} type={type} />
      </>
    )}
    <div className="absolute z-10 -right-8 -top-4">
      <RewardProgressBarIcon amount={progress.nextDing} side="end" type={type} size={50} />
    </div>
  </div>
)

export default RewardProgressBar
